import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Database as DbIcon, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CATEGORIES, CATEGORY_COLORS } from "@/lib/meta";

const Field = ({ label, value }) =>
  value ? (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-mono text-sm text-white mt-0.5">{value}</div>
    </div>
  ) : null;

export default function DatabasePage() {
  const [materials, setMaterials] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const res = await api.get("/materials", { params: { search: search || undefined, category } });
    setMaterials(res.data);
    setLoading(false);
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, category]);

  return (
    <div className="grain mx-auto max-w-[1400px] px-4 sm:px-6 py-8">
      <div className="flex items-center gap-3 mb-6">
        <DbIcon size={26} weight="bold" className="text-primary" />
        <div>
          <h1 className="font-heading font-black text-white text-2xl">Materials Database</h1>
          <p className="text-sm text-muted-foreground">Curated feedstock library — metals, ceramics, cermets, composites & MMCs.</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="db-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search materials..."
            className="pl-9 rounded-sm bg-card border-border font-mono text-sm focus-visible:ring-primary"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="db-category" className="w-full sm:w-52 rounded-sm bg-card border-border font-mono text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            {["All", ...CATEGORIES].map((c) => (
              <SelectItem key={c} value={c} className="font-mono text-sm">{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="font-mono text-sm text-muted-foreground">loading<span className="cursor-blink">_</span></div>
      ) : materials.length === 0 ? (
        <div className="border border-border bg-card p-10 text-center text-muted-foreground">No materials found.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {materials.map((m) => (
            <Link
              key={m.id}
              to={`/material/${m.id}`}
              data-testid={`db-card-${m.id}`}
              className="group block border border-border bg-card p-4 hover:border-primary/60 hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <h3 className="font-heading font-bold text-white text-base leading-tight group-hover:text-primary transition-colors">{m.name}</h3>
                <span className={`shrink-0 border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wide rounded-sm ${CATEGORY_COLORS[m.category] || CATEGORY_COLORS.Other}`}>
                  {m.category}
                </span>
              </div>
              {m.composition && <div className="font-mono text-xs text-secondary mb-3">{m.composition}</div>}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {m.recommended_processes?.map((p) => (
                  <span key={p} className="border border-border bg-background px-2 py-0.5 text-[10px] font-mono text-muted-foreground rounded-sm">{p}</span>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Hardness" value={m.hardness} />
                <Field label="Porosity" value={m.porosity} />
                <Field label="Bond Strength" value={m.bond_strength} />
                <Field label="Density" value={m.density} />
                <Field label="Melting Point" value={m.melting_point} />
              </div>
              {m.applications && (
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Applications</div>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{m.applications}</p>
                </div>
              )}
              <div className="mt-3 flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-colors">
                View details <ArrowRight size={12} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
